/**
 * Cross-tab communication utility for real-time updates
 * Uses localStorage events to communicate between tabs/windows
 * This is a fallback when Socket.IO is not available
 */

const EVENTS = {
  APPOINTMENT_BOOKED: 'hms:appointment:booked',
  APPOINTMENT_UPDATED: 'hms:appointment:updated',
  QUEUE_REFRESH: 'hms:queue:refresh',
};

class CrossTabCommunication {
  constructor() {
    this.listeners = new Map();
    this.setupStorageListener();
  }

  setupStorageListener() {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (e) => {
      if (!e.key || !e.key.startsWith('hms:')) return;

      const handlers = this.listeners.get(e.key);
      if (handlers) {
        try {
          const data = e.newValue ? JSON.parse(e.newValue) : null;
          handlers.forEach(handler => handler(data));
        } catch (err) {
          console.error('Error parsing cross-tab message:', err);
        }
      }
    });
  }

  /**
   * Emit an event to other tabs
   */
  emit(event, data) {
    if (typeof window === 'undefined') return;

    const message = {
      ...data,
      timestamp: Date.now(),
    };

    // Set then immediately remove to trigger storage event in other tabs
    localStorage.setItem(event, JSON.stringify(message));
    setTimeout(() => localStorage.removeItem(event), 100);
  }

  /**
   * Listen to events from other tabs
   */
  on(event, handler) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(handler);
  }

  /**
   * Remove event listener
   */
  off(event, handler) {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Notify that an appointment was booked
   */
  notifyAppointmentBooked(appointment) {
    this.emit(EVENTS.APPOINTMENT_BOOKED, {
      appointmentId: appointment._id || appointment.id,
      doctorId: appointment.doctor || appointment.doctorId,
      patientName: appointment.patientName,
      appointmentDate: appointment.appointmentDate,
    });
  }

  /**
   * Notify that queue should be refreshed
   */
  notifyQueueRefresh(doctorId) {
    this.emit(EVENTS.QUEUE_REFRESH, { doctorId });
  }

  /**
   * Listen for appointment bookings
   */
  onAppointmentBooked(handler) {
    this.on(EVENTS.APPOINTMENT_BOOKED, handler);
  }

  /**
   * Listen for queue refresh events
   */
  onQueueRefresh(handler) {
    this.on(EVENTS.QUEUE_REFRESH, handler);
  }
}

// Singleton instance
const crossTab = new CrossTabCommunication();

export { EVENTS, crossTab };
export default crossTab;
